import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, SlidersHorizontal, Star, MapPin, X, Clock, Truck, Home as HomeIcon, MessageCircle, Tag, Briefcase, DollarSign, ChevronDown, Mountain, LayoutGrid, List, Layers } from 'lucide-react';
import { useSearchParams, useNavigate, useParams, Link } from 'react-router-dom';
import BusinessCard from '../components/BusinessCard';
import CategoryCard from '../components/CategoryCard';
import HorizontalCarousel from '../components/HorizontalCarousel';
import { useLocation } from '../context/LocationContext';
import useTenantGeoRedirect from '../hooks/useTenantGeoRedirect';
import Breadcrumbs from '../components/Breadcrumbs';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
// Use centralized API service
import { 
  businessesApi, 
  categoriesApi, 
  bairrosApi, 
  territoriesApi,
  municipiosApi,
  searchApi,
  api as apiClient,
} from '../services/api';
import SEOHead from '../components/SEOHead';
import { useDebounce } from '../hooks/useDebounce';

export default function Explorar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { municipioSlug, categoriaSlug, secondSlug } = useParams();
  // secondSlug pode ser uma subcategoria quando vier via rota
  // /:municipioSlug/:categoriaSlug/:secondSlug e o resolver determinou que é
  // subcategoria (caso contrário renderiza NegocioDetalhes).
  const subcategoriaFromPath = secondSlug || null;
  const { location } = useLocation();
  const bairroRef = useRef(null);
  const distritoRef = useRef(null);
  
  // Aceita "busca" (canônico) e "q" (alias — usado em URLs curtas / deep links externos)
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('busca') || searchParams.get('q') || ''
  );
  const debouncedSearchQuery = useDebounce(searchQuery, 400);
  // SEO route: /:municipio/:categoriaSlug ←→ ?categoria= no Explorar tradicional.
  // categoriaSlug do path tem prioridade sobre ?categoria= (rota canônica).
  const [selectedCategory, setSelectedCategory] = useState(categoriaSlug || searchParams.get('categoria') || '');
  const [categories, setCategories] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [municipio, setMunicipio] = useState(null);
  
  // Bairros
  const [bairros, setBairros] = useState([]);
  const [selectedBairro, setSelectedBairro] = useState(searchParams.get('bairro') || '');
  const [selectedTags, setSelectedTags] = useState(() => {
    const raw = searchParams.get('tags');
    return raw ? raw.split(',').filter(Boolean) : [];
  });
  const [availableFilterTags, setAvailableFilterTags] = useState([]);
  const [showBairroDropdown, setShowBairroDropdown] = useState(false);
  
  // Distritos/Territórios
  const [distritos, setDistritos] = useState([]);
  const [selectedDistrito, setSelectedDistrito] = useState(searchParams.get('distrito') || '');
  const [showDistritoDropdown, setShowDistritoDropdown] = useState(false);

  const [filters, setFilters] = useState({
    entrega: searchParams.get('entrega') === 'true',
    atende_domicilio: searchParams.get('atende_domicilio') === 'true',
    promocao: searchParams.get('promocao') === 'true',
    aberto_agora: searchParams.get('aberto_agora') === 'true',
    esta_contratando: searchParams.get('esta_contratando') === 'true',
    novos: false,
    avaliacaoMinima: 0,
    distanciaMaxima: 0,
    faixa_preco: searchParams.get('faixa_preco') || ''
  });

  const [ordenarPor, setOrdenarPor] = useState('distancia'); // distancia, avaliacao, recente

  // View mode (grid|list) — persists user preference across visits.
  // No mobile (<sm ~ 640px) o default é 'list' — cards em linha única
  // dão melhor scanabilidade em tela estreita. Desktop mantém 'grid'.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem('explorar_view_mode');
      if (saved === 'list' || saved === 'grid') return saved;
      // Default por breakpoint quando o usuário nunca escolheu
      if (typeof window !== 'undefined' && window.innerWidth < 640) return 'list';
      return 'grid';
    } catch {
      return 'grid';
    }
  });
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem('explorar_view_mode', mode); } catch { /* ignore */ }
  };

  // Collapse the sticky filters bar after the user scrolls past the page header.
  // We use an IntersectionObserver on a sentinel element placed *above* the
  // sticky bar (inside the breadcrumbs strip) instead of listening to scrollY.
  // Why: collapsing the bar shrinks the document, which kicks scrollY back
  // below the threshold, which expands the bar, which... loops forever.
  // The sentinel's position is stable, so its visibility transition is binary
  // and free of feedback.
  const sentinelRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [userExpanded, setUserExpanded] = useState(false);
  const [slugValid, setSlugValid] = useState(true);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        const isStuck = !entry.isIntersecting;
        setScrolled(isStuck);
        if (!isStuck) setUserExpanded(false);
      },
      { rootMargin: '-64px 0px 0px 0px', threshold: 0 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  const filtersHidden = scrolled && !userExpanded;

  // When the result set is empty there is rarely enough page height to scroll,
  // so force the sticky filter bar back to its expanded state — otherwise it
  // could remain collapsed from a previous scroll position with no way out.
  useEffect(() => {
    if (!loading && filteredBusinesses.length === 0 && scrolled) {
      setScrolled(false);
      setUserExpanded(false);
    }
  }, [loading, filteredBusinesses.length, scrolled]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bairroRef.current && !bairroRef.current.contains(event.target)) {
        setShowBairroDropdown(false);
      }
      if (distritoRef.current && !distritoRef.current.contains(event.target)) {
        setShowDistritoDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadCategories();
    loadBairros();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load municipality info and distritos
  useEffect(() => {
    const loadMunicipio = async () => {
      if (municipioSlug) {
        try {
          const allMunicipios = await municipiosApi.list();
          const found = allMunicipios.find(m => m.slug === municipioSlug);
          if (found) {
            setMunicipio(found);
            // Load distritos for this municipality
            loadDistritos(found.slug);
          }
        } catch (e) {
          console.error('Error loading municipio:', e);
        }
      }
    };
    loadMunicipio();
  }, [municipioSlug]);

  // Legacy /explorar (no slug): redirect to /<slug>/explorar respecting:
  // user choice → cached geo → fresh geo. NEVER overrides user's pick.
  useTenantGeoRedirect({ routeName: 'explorar', currentSlug: municipioSlug });

  const loadDistritos = async (slug) => {
    try {
      const data = await territoriesApi.distritos(slug);
      setDistritos(data || []);
    } catch (error) {
      console.error('Erro ao carregar distritos:', error);
      setDistritos([]);
    }
  };

  // Auto-scroll the categories carousel to bring the active category into view
  // so the user has a clear visual confirmation that the filter is applied.
  useEffect(() => {
    if (!selectedCategory || categories.length === 0) return;
    const tries = [50, 250, 700];
    const timers = tries.map(d => setTimeout(() => {
      const el = document.querySelector(`[data-cat-id="${selectedCategory}"]`);
      if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }, d));
    return () => timers.forEach(clearTimeout);
  }, [selectedCategory, categories]);

  // Carrega tags disponíveis quando categoria/subcategoria muda
  useEffect(() => {
    if (!selectedCategory && !subcategoriaFromPath) {
      setAvailableFilterTags([]);
      setSelectedTags([]);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const params = { limit: 30 };
        if (subcategoriaFromPath) {
          // subcategoria_slug vem como "restaurantes" — precisamos construir o id composto
          params.subcategoria_id = `${selectedCategory}__${subcategoriaFromPath}`;
        } else if (selectedCategory) {
          params.categoria_id = selectedCategory;
        }
        const r = await apiClient.get('/tags/buscar', { params });
        if (!alive) return;
        setAvailableFilterTags(r?.data?.items || []);
      } catch (_e) {
        if (alive) setAvailableFilterTags([]);
      }
    })();
    return () => { alive = false; };
  }, [selectedCategory, subcategoriaFromPath]);


  useEffect(() => {
    loadBusinesses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, subcategoriaFromPath, debouncedSearchQuery, filters, location, ordenarPor, selectedBairro, selectedDistrito, selectedTags]);

  // SEO consolidation: redirect legacy `?categoria=X` (within a municipio scope)
  // to the canonical `/:municipio/:categoria-slug` URL. Equivalent to a 301
  // for SPA traffic. Only fires when:
  //   - we're inside a municipio (`municipioSlug` present),
  //   - the URL has `?categoria=` (legacy),
  //   - we're NOT already on a /:municipio/:slug path,
  //   - the query value resolves to a known category.
  useEffect(() => {
    if (!municipioSlug || categoriaSlug) return;
    const qCat = searchParams.get('categoria');
    if (!qCat || categories.length === 0) return;
    const found = categories.find(c => c.id === qCat || c.slug === qCat);
    if (!found) return;
    const slug = found.slug || found.id;
    // Preserve other filters (busca, bairro, distrito, etc.) via querystring.
    const params = new URLSearchParams(searchParams);
    params.delete('categoria');
    const qs = params.toString();
    navigate(`/${municipioSlug}/${slug}${qs ? `?${qs}` : ''}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipioSlug, categoriaSlug, categories, searchParams]);

  // Resolve um categoriaSlug do PATH (ex.: "construcao-reformas") para o ID
  // real da categoria ("construcao") quando as categories carregarem. Isso
  // garante que o filtro use o ID (que é o que o backend indexa) e que o
  // card correto fique destacado no carrossel.
  useEffect(() => {
    if (!categoriaSlug || categories.length === 0) return;
    const found = categories.find(
      c => c.id === categoriaSlug || c.slug === categoriaSlug,
    );
    if (found) {
      if (found.id !== selectedCategory) setSelectedCategory(found.id);
      setSlugValid(true);
    } else {
      setSlugValid(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriaSlug, categories]);

  const loadCategories = async () => {
    try {
      const data = await categoriesApi.list();
      setCategories(data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadBairros = async () => {
    try {
      const data = await bairrosApi.list(municipioSlug);
      setBairros(data);
    } catch (error) {
      console.error('Erro ao carregar bairros:', error);
    }
  };

  const loadBusinesses = async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (selectedCategory) params.categoria = selectedCategory;
      if (subcategoriaFromPath) params.subcategoria_slug = subcategoriaFromPath;
      if (debouncedSearchQuery) params.busca = debouncedSearchQuery;
      if (selectedBairro) params.bairro = selectedBairro;
      if (selectedDistrito) params.territory_id = selectedDistrito;
      // Filtro por tags controladas (múltiplas — enviadas como CSV que o backend
      // interpreta em qualquer ordem)
      if (selectedTags && selectedTags.length > 0) {
        params.tags_controladas = selectedTags.join(',');
      }
      if (filters.entrega) params.entrega = true;
      if (filters.atende_domicilio) params.atende_domicilio = true;
      if (filters.promocao) params.promocao = true;
      if (filters.aberto_agora) params.aberto_agora = true;
      if (filters.esta_contratando) params.esta_contratando = true;
      if (filters.novos) params.novos = true;
      if (filters.faixa_preco) params.faixa_preco = filters.faixa_preco;
      
      if (location) {
        params.lat = location.latitude;
        params.lon = location.longitude;
      }

      if (ordenarPor) {
        params.ordenar_por = ordenarPor;
      }

      // Add municipality slug for tenant filtering
      if (municipioSlug) {
        params.municipio_slug = municipioSlug;
      }

      // Use v2 API
      const data = await businessesApi.list(params);
      setBusinesses(data);
      applyLocalFilters(data);
      
      // Log search for analytics (non-blocking)
      if (selectedCategory || searchQuery) {
        searchApi.log(
          searchQuery || null,
          data.length,
          null,
          { categoria: selectedCategory || null, bairro: selectedBairro || null, distrito: selectedDistrito || null }
        ).catch(() => {}); // Silently fail
      }
    } catch (error) {
      console.error('Erro ao carregar negócios:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply local filters (rating, distance) that can't be done server-side
  const applyLocalFilters = (data) => {
    let filtered = [...data];

    // Filter by minimum rating
    if (filters.avaliacaoMinima > 0) {
      filtered = filtered.filter(b => 
        (b.media_avaliacoes || 0) >= filters.avaliacaoMinima
      );
    }

    // Filter by max distance (if location available)
    if (filters.distanciaMaxima > 0 && location) {
      filtered = filtered.filter(b => 
        (b.distancia_km || 999) <= filters.distanciaMaxima
      );
    }

    setFilteredBusinesses(filtered);
  };

  // Reapply filters when they change
  useEffect(() => {
    applyLocalFilters(businesses);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.avaliacaoMinima, filters.distanciaMaxima]);

  const activeFiltersCount = Object.entries(filters).filter(([key, val]) => {
    if (key === 'avaliacaoMinima') return val > 0;
    if (key === 'distanciaMaxima') return val > 0;
    if (key === 'faixa_preco') return val !== '';
    return val === true;
  }).length + (selectedBairro ? 1 : 0) + (selectedDistrito ? 1 : 0);

  const clearAllFilters = () => {
    setFilters({
      entrega: false,
      atende_domicilio: false,
      promocao: false,
      aberto_agora: false,
      esta_contratando: false,
      novos: false,
      avaliacaoMinima: 0,
      distanciaMaxima: 0,
      faixa_preco: ''
    });
    setSelectedCategory('');
    setSelectedBairro('');
    setSelectedDistrito('');
    setSearchQuery('');
    setSearchParams({});
  };

  const handleCategorySelect = (categoryId) => {
    const newCategory = selectedCategory === categoryId ? '' : categoryId;
    setSelectedCategory(newCategory);
    updateSearchParams({ categoria: newCategory });
    // Reset scroll so the sticky filter bar restores its expanded layout when
    // the result set shrinks (e.g. category with 0 results would otherwise
    // leave the bar in its "scrolled/collapsed" state with nothing to scroll).
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleFilter = (filterKey) => {
    const newValue = !filters[filterKey];
    setFilters(prev => ({ ...prev, [filterKey]: newValue }));
    updateSearchParams({ [filterKey]: newValue ? 'true' : null });
  };

  const updateSearchParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateSearchParams({ busca: searchQuery || null });
  };

  // 404-like state when categoriaSlug doesn't match any known category.
  // This guard runs AFTER all hooks to comply with React's Rules of Hooks.
  if (categoriaSlug && categories.length > 0 && !slugValid) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 py-20" data-testid="categoria-not-found">
        <div className="text-6xl mb-4">🔍</div>
        <h1 className="text-2xl font-bold mb-2 text-[var(--text-primary)]">Categoria não encontrada</h1>
        <p className="text-[var(--text-secondary)] max-w-md mb-6">
          A categoria <strong>{categoriaSlug}</strong> não existe{municipio ? ` em ${municipio.nome}` : ''}.
          Confira o link ou explore todas as categorias disponíveis.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => navigate(municipioSlug ? `/${municipioSlug}/explorar` : '/explorar')}
            className="px-5 py-2.5 bg-[var(--primary)] text-white rounded-lg font-medium hover:opacity-90"
            data-testid="btn-back-explorar"
          >
            Ver todos os negócios
          </button>
          <button
            onClick={() => navigate('/categorias')}
            className="px-5 py-2.5 border border-[var(--border)] rounded-lg font-medium hover:bg-gray-50"
            data-testid="btn-listar-categorias"
          >
            Listar categorias
          </button>
        </div>
      </div>
    );
  }

  // Human-friendly name of the active subcategoria (resolves slug → label)
  const subcategoriaName = (() => {
    if (!subcategoriaFromPath) return null;
    const cat = selectedCategory ? categories.find(c => c.id === selectedCategory) : null;
    if (!cat) return subcategoriaFromPath;
    const norm = (x) => (x || '').toString().normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '').toLowerCase()
      .replace(/[^\w\s-]/g, '').replace(/[-\s]+/g, '-').replace(/^-+|-+$/g, '');
    return (cat.subcategorias || []).find(s => norm(s) === subcategoriaFromPath) || subcategoriaFromPath;
  })();

  // Subcategorias relacionadas: contagem por subcategoria a partir dos resultados
  // atuais. Só visível quando estamos na página de categoria (não em subcategoria)
  // e há subcategorias cadastradas para a categoria.
  const subcategoriaChips = (() => {
    if (!selectedCategory || subcategoriaFromPath) return [];
    const cat = categories.find(c => c.id === selectedCategory);
    const subs = cat?.subcategorias || [];
    if (subs.length === 0) return [];
    const norm = (x) => (x || '').toString().normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '').toLowerCase()
      .replace(/[^\w\s-]/g, '').replace(/[-\s]+/g, '-').replace(/^-+|-+$/g, '');
    // Count businesses per subcategoria (case-insensitive match on the canonical name).
    // Compat: negócios podem guardar em `subcategoria` (string legada) OU
    // em `subcategorias` (array novo, multi-select) OU em ambos.
    const counts = {};
    for (const biz of filteredBusinesses) {
      const bizSubs = new Set();
      if (biz.subcategoria) bizSubs.add(biz.subcategoria);
      if (Array.isArray(biz.subcategorias)) {
        for (const s of biz.subcategorias) {
          if (s) bizSubs.add(s);
        }
      }
      for (const bizSub of bizSubs) {
        const match = subs.find(s => norm(s) === norm(bizSub));
        if (match) counts[match] = (counts[match] || 0) + 1;
      }
    }
    const cat_slug = cat?.slug || selectedCategory;
    // If we have any real counts, show only those (high-fidelity).
    // Otherwise show all subcategorias as discovery chips (mock-data fallback).
    const hasCounts = Object.keys(counts).length > 0;
    return subs
      .filter(s => !hasCounts || counts[s] > 0)
      .map(s => ({
        label: s,
        slug: norm(s),
        count: counts[s] || 0,
        url: `/${municipioSlug || ''}/${cat_slug}/${norm(s)}`,
      }))
      .sort((a, b) => (b.count - a.count) || a.label.localeCompare(b.label, 'pt-BR'))
      .slice(0, 12);
  })();

  // JSON-LD ItemList + LocalBusiness for rich snippets when filtering by
  // category (canonical SEO page). Only emit when we have a municipality and
  // a selected category, and there are businesses to list.
  const jsonLd = (() => {
    if (!municipio || !selectedCategory || filteredBusinesses.length === 0) return null;
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const cat = categories.find(c => c.id === selectedCategory);
    const slug = cat?.slug || selectedCategory;
    const subSlugPart = subcategoriaFromPath ? `/${subcategoriaFromPath}` : '';
    const listUrl = `${baseUrl}/${municipioSlug}/${slug}${subSlugPart}`;
    const itemList = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `${subcategoriaName ? `${subcategoriaName} — ` : ''}${cat?.nome || selectedCategory} em ${municipio.nome}`,
      url: listUrl,
      numberOfItems: filteredBusinesses.length,
      itemListElement: filteredBusinesses.slice(0, 25).map((b, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'LocalBusiness',
          name: b.nome,
          url: `${baseUrl}/${b.municipio_slug || municipioSlug}/${b.categoria_slug || b.categoria}/${b.slug || b.id}`,
          ...(b.foto_capa ? { image: b.foto_capa } : {}),
          ...(b.descricao_curta ? { description: b.descricao_curta } : {}),
          ...(b.telefone ? { telephone: b.telefone } : {}),
          ...(b.bairro || b.municipio_nome
            ? {
                address: {
                  '@type': 'PostalAddress',
                  ...(b.bairro ? { addressLocality: b.bairro } : {}),
                  addressRegion: municipio.uf || 'MG',
                  addressCountry: 'BR',
                },
              }
            : {}),
          ...(typeof b.avaliacao_media === 'number' && b.avaliacao_media > 0
            ? {
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: b.avaliacao_media,
                  reviewCount: b.total_avaliacoes || 1,
                },
              }
            : {}),
        },
      })),
    };
    return itemList;
  })();

  return (
    <div className="min-h-screen bg-[var(--background)] pb-20">
      <SEOHead
        title={municipio
          ? (selectedCategory
              ? `${subcategoriaName ? `${subcategoriaName} — ` : ''}${categories.find(c => c.id === selectedCategory)?.nome || selectedCategory} em ${municipio.nome}, ${municipio.uf || 'MG'}`
              : `Explorar negócios em ${municipio.nome}, ${municipio.uf || 'MG'}`)
          : 'Explorar Negócios'}
        description={municipio
          ? `Explore todos os negócios e serviços disponíveis em ${municipio.nome}, ${municipio.uf || 'MG'}. Filtre por categoria, bairro e muito mais.`
          : 'Explore negócios e serviços locais. Filtre por categoria, localização e encontre o que precisa.'
        }
        canonical={(() => {
          if (!municipioSlug) return selectedCategory ? `/explorar?categoria=${selectedCategory}` : '/explorar';
          if (selectedCategory) {
            const cat = categories.find(c => c.id === selectedCategory);
            const slug = cat?.slug || selectedCategory;
            const subPart = subcategoriaFromPath ? `/${subcategoriaFromPath}` : '';
            return `/${municipioSlug}/${slug}${subPart}`;
          }
          return `/${municipioSlug}/explorar`;
        })()}
        jsonLd={jsonLd}
        noindex={!!searchParams.get('busca') || !!searchParams.get('bairro')}
      />
      {/* Header with Breadcrumbs */}
      <div className="bg-gradient-to-r from-[var(--primary)] to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <Breadcrumbs 
            items={(() => {
              const cat = selectedCategory ? categories.find(c => c.id === selectedCategory) : null;
              const catSlug = cat?.slug || selectedCategory;
              return [
                { label: 'Explorar', path: (selectedCategory || subcategoriaFromPath) ? `/${municipioSlug || ''}/explorar` : null },
                ...(selectedCategory
                  ? [{
                      label: cat?.nome || selectedCategory,
                      // When viewing a subcategory, the parent category is clickable
                      // to drill back up. Otherwise it's the current page (no path).
                      path: subcategoriaFromPath ? `/${municipioSlug || ''}/${catSlug}` : null,
                    }]
                  : []),
                ...(subcategoriaName
                  ? [{ label: subcategoriaName, path: null }]
                  : []),
              ];
            })()}
            municipio={municipio}
          />
        </div>
      </div>
      {/* Sentinel for IntersectionObserver — stays in document flow at a fixed
          position so collapsing the sticky bar doesn't oscillate it in/out of
          view. */}
      <div ref={sentinelRef} aria-hidden="true" className="h-px w-full" />
      
      <div className={`bg-white border-b border-[var(--border)] sticky top-14 sm:top-16 z-30 transition-shadow duration-200 ${scrolled ? 'shadow-md' : ''}`}>
        <div className={`max-w-7xl mx-auto px-4 transition-[padding] duration-200 ${scrolled ? 'py-1.5' : 'py-2'}`}>
          {/* H1 removido a pedido — a categoria é comunicada via breadcrumb.
              Mantemos o H1 escondido apenas para acessibilidade/SEO. */}
          <h1 className="sr-only" data-testid="explorar-h1">
            {selectedCategory
              ? `${subcategoriaName ? `${subcategoriaName} — ` : ''}${categories.find(c => c.id === selectedCategory)?.nome || selectedCategory}`
              : 'Explorar negócios'}
          </h1>

          {/* Chips de subcategorias relacionadas — só aparecem na página de categoria.
              No mobile (<sm) vira um <select> compacto; no desktop mantém chips. */}
          {subcategoriaChips.length > 0 && !scrolled && (
            <>
              {/* Mobile: select nativo (bottom sheet do sistema) */}
              <div className="sm:hidden flex items-center gap-2 mb-3 -mt-1" data-testid="subcategoria-select-mobile">
                <span className="text-xs text-[var(--text-secondary)] whitespace-nowrap">
                  Refinar:
                </span>
                <select
                  value=""
                  onChange={(e) => { if (e.target.value) navigate(e.target.value); }}
                  className="flex-1 min-w-0 px-2.5 py-1.5 bg-[var(--background-elevated)] border border-[var(--border)] text-xs rounded-full text-[var(--text-secondary)] focus:outline-none focus:border-[var(--primary)]"
                  data-testid="subcategoria-select"
                >
                  <option value="">Todas as subcategorias</option>
                  {subcategoriaChips.map(s => (
                    <option key={s.slug} value={s.url}>
                      {s.label}{s.count > 0 ? ` (${s.count})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Desktop: chips originais */}
              <div className="hidden sm:flex flex-wrap gap-1.5 mb-3 -mt-1" data-testid="subcategoria-chips">
                <span className="text-xs text-[var(--text-secondary)] py-1 self-center mr-1">
                  Refinar:
                </span>
                {subcategoriaChips.map(s => (
                  <Link
                    key={s.slug}
                    to={s.url}
                    className="px-2.5 py-1 bg-[var(--background-elevated)] border border-[var(--border)] text-xs rounded-full text-[var(--text-secondary)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
                    data-testid={`sub-chip-${s.slug}`}
                  >
                    {s.label}
                    {s.count > 0 && <span className="ml-1 text-[10px] opacity-60">({s.count})</span>}
                  </Link>
                ))}
              </div>
            </>
          )}

          {/* Search Bar with Button */}
          <form onSubmit={handleSearch} className={`flex gap-2 transition-[margin] duration-200 ${scrolled ? 'mb-1.5' : 'mb-2'}`}>
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
              <input
                type="text"
                placeholder="Buscar por nome, serviço ou produto..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-12 pr-12 rounded-xl border border-[var(--border)] bg-white focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all ${scrolled ? 'py-2' : 'py-3'}`}
                data-testid="explorar-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className={`px-5 bg-[var(--primary)] text-white rounded-xl font-medium hover:bg-[var(--primary)]/90 transition-all flex items-center gap-2 ${scrolled ? 'py-2' : 'py-3'}`}
              data-testid="explorar-search-btn"
            >
              <Search className="w-5 h-5" />
              <span className="hidden sm:inline">Buscar</span>
            </button>
            {scrolled && (
              <button
                type="button"
                onClick={() => setUserExpanded(v => !v)}
                className={`px-3 sm:px-4 rounded-xl font-medium border transition-all flex items-center gap-2 py-2 ${
                  userExpanded
                    ? 'bg-[var(--primary)]/10 border-[var(--primary)] text-[var(--primary)]'
                    : 'bg-white border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--primary)]'
                }`}
                aria-label="Mostrar filtros"
                data-testid="explorar-toggle-filters"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Filtros</span>
                {activeFiltersCount > 0 && (
                  <span className="bg-[var(--primary)] text-white text-xs rounded-full min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center" data-testid="explorar-filter-count">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            )}
          </form>

          {/* Collapsible secondary filters: bairro/distrito + chips + ordenar */}
          <div
            className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
              filtersHidden ? 'max-h-0 opacity-0 pointer-events-none' : 'max-h-[600px] opacity-100'
            }`}
            data-testid="explorar-secondary-filters"
            aria-hidden={filtersHidden}
          >

          {/* Bairro and Distrito Filters — no mobile vira scroll horizontal
              pra caber todos os controles em uma linha só (sem wrap).
              Desktop mantém flex-wrap normal. */}
          <div
            className={`flex flex-nowrap sm:flex-wrap gap-2 overflow-x-auto sm:overflow-visible scrollbar-hide pb-1 sm:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0 transition-[margin] duration-200 ${scrolled ? 'mb-1' : 'mb-2'}`}
          >
            {/* Distrito Filter - Only show if municipality has distritos */}
            {distritos.length > 0 && (
              <div className="relative flex-shrink-0" ref={distritoRef}>
                <button
                  onClick={() => setShowDistritoDropdown(!showDistritoDropdown)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
                    selectedDistrito 
                      ? 'border-purple-500 bg-purple-50 text-purple-700' 
                      : 'border-[var(--border)] bg-white text-[var(--text-secondary)] hover:border-purple-500'
                  }`}
                  data-testid="distrito-filter-btn"
                >
                  <Mountain className="w-4 h-4" />
                  <span className="font-medium text-sm">
                    {selectedDistrito 
                      ? distritos.find(d => d.id === selectedDistrito)?.nome || 'Distrito'
                      : 'Filtrar por distrito'}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showDistritoDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showDistritoDropdown && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl border border-[var(--border)] shadow-lg z-50 max-h-64 overflow-y-auto">
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setSelectedDistrito('');
                          setShowDistritoDropdown(false);
                          updateSearchParams({ distrito: null });
                        }}
                        className={`w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg text-sm ${
                          !selectedDistrito ? 'bg-purple-50 text-purple-700 font-medium' : ''
                        }`}
                      >
                        Todos os distritos
                      </button>
                      {distritos.map(distrito => (
                        <button
                          key={distrito.id}
                          onClick={() => {
                            setSelectedDistrito(distrito.id);
                            setShowDistritoDropdown(false);
                            updateSearchParams({ distrito: distrito.id });
                          }}
                          className={`w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-2 text-sm ${
                            selectedDistrito === distrito.id ? 'bg-purple-50 text-purple-700 font-medium' : ''
                          }`}
                        >
                          {distrito.tipo === 'sede' ? (
                            <MapPin className="w-4 h-4" />
                          ) : (
                            <Mountain className="w-4 h-4" />
                          )}
                          {distrito.nome}
                          <span className="text-xs text-gray-400 ml-auto">
                            {distrito.tipo === 'sede' ? 'Sede' : 'Distrito'}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedDistrito && (
              <button
                onClick={() => {
                  setSelectedDistrito('');
                  updateSearchParams({ distrito: null });
                }}
                className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            {/* Bairro Filter */}
            <div className="relative flex-shrink-0" ref={bairroRef}>
              <button
                onClick={() => setShowBairroDropdown(!showBairroDropdown)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
                  selectedBairro 
                    ? 'border-[var(--primary)] bg-[var(--primary)]/5 text-[var(--primary)]' 
                    : 'border-[var(--border)] bg-white text-[var(--text-secondary)] hover:border-[var(--primary)]'
                }`}
                data-testid="bairro-filter-btn"
              >
                <MapPin className="w-4 h-4" />
                <span className="font-medium text-sm">
                  {selectedBairro || 'Filtrar por bairro'}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showBairroDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showBairroDropdown && bairros.length > 0 && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl border border-[var(--border)] shadow-lg z-50 max-h-64 overflow-y-auto">
                  <div className="p-2">
                    <button
                      onClick={() => {
                        setSelectedBairro('');
                        setShowBairroDropdown(false);
                        updateSearchParams({ bairro: null });
                      }}
                      className={`w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg text-sm ${
                        !selectedBairro ? 'bg-[var(--primary)]/10 text-[var(--primary)] font-medium' : ''
                      }`}
                    >
                      Todos os bairros
                    </button>
                    {bairros.map(bairro => (
                      <button
                        key={bairro}
                        onClick={() => {
                          setSelectedBairro(bairro);
                          setShowBairroDropdown(false);
                          updateSearchParams({ bairro });
                        }}
                        className={`w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-2 text-sm ${
                          selectedBairro === bairro ? 'bg-[var(--primary)]/10 text-[var(--primary)] font-medium' : ''
                        }`}
                      >
                        <MapPin className="w-4 h-4" />
                        {bairro}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {selectedBairro && (
              <button
                onClick={() => {
                  setSelectedBairro('');
                  updateSearchParams({ bairro: null });
                }}
                className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            {/* Filtro por Especialização — só aparece depois de escolher categoria.
                Multi-select via Popover + Checkbox (visual de <Select>) para
                substituir o <details> antigo. */}
            {(selectedCategory || subcategoriaFromPath) && availableFilterTags.length > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm min-w-[160px] justify-between"
                    data-testid="explorar-tags-filter-toggle"
                  >
                    <span className="flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      {selectedTags.length > 0
                        ? `${selectedTags.length} especialização${selectedTags.length === 1 ? '' : 'ões'}`
                        : 'Especialização'}
                    </span>
                    <ChevronDown className="w-4 h-4 opacity-60" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-64 p-2 max-h-72 overflow-y-auto"
                  data-testid="explorar-tags-filter-panel"
                >
                  {selectedTags.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedTags([])}
                      className="w-full text-left px-2 py-1.5 text-xs text-[var(--primary)] hover:bg-gray-50 rounded flex items-center gap-1"
                      data-testid="explorar-tags-clear"
                    >
                      <X className="w-3 h-3" /> Limpar seleção
                    </button>
                  )}
                  {availableFilterTags.map((t) => {
                    const active = selectedTags.includes(t.id);
                    return (
                      <label
                        key={t.id}
                        htmlFor={`tag-opt-${t.id}`}
                        className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 cursor-pointer ${
                          active ? 'bg-[var(--primary)]/10 text-[var(--primary)] font-medium' : 'hover:bg-gray-50'
                        }`}
                        data-testid={`explorar-tag-option-${t.id}`}
                      >
                        <Checkbox
                          id={`tag-opt-${t.id}`}
                          checked={active}
                          onCheckedChange={() => {
                            setSelectedTags((prev) =>
                              active ? prev.filter((x) => x !== t.id) : [...prev, t.id]
                            );
                          }}
                        />
                        <span className="flex-1">{t.nome}</span>
                        {t.tipo === 'marca' && <span className="text-xs text-gray-400">marca</span>}
                      </label>
                    );
                  })}
                </PopoverContent>
              </Popover>
            )}
            {selectedTags.length > 0 && (
              <div className="flex flex-wrap gap-1 items-center" data-testid="explorar-tags-selected">
                {selectedTags.map((tid) => {
                  const t = availableFilterTags.find((x) => x.id === tid);
                  return (
                    <span
                      key={tid}
                      className="inline-flex items-center gap-1 bg-[var(--primary)]/10 text-[var(--primary)] px-2 py-0.5 rounded-full text-xs font-medium"
                    >
                      {t?.nome || tid}
                      <button
                        onClick={() => setSelectedTags(selectedTags.filter((x) => x !== tid))}
                        className="hover:bg-[var(--primary)]/20 rounded-full p-0.5"
                        aria-label={`Remover ${t?.nome || tid}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Contratando — promovido para a linha do filtro de Bairro (fase 1 #4) */}
            <button
              onClick={() => toggleFilter('esta_contratando')}
              className={`flex-shrink-0 whitespace-nowrap flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                filters.esta_contratando
                  ? 'border-[var(--primary)] bg-[var(--primary)]/5 text-[var(--primary)]'
                  : 'border-[var(--border)] bg-white text-[var(--text-secondary)] hover:border-[var(--primary)]'
              }`}
              data-testid="filter-chip-esta_contratando"
            >
              <Briefcase className="w-4 h-4" />
              Contratando
            </button>

            {/* Filtros rápidos (Popover) — inline com os demais filtros */}
            <div className="flex-shrink-0">
              <FiltrosRapidos
                filters={filters}
                toggleFilter={toggleFilter}
                setFilters={setFilters}
                updateSearchParams={updateSearchParams}
              />
            </div>

            {/* Ordenar — inline com os demais filtros */}
            <div className="flex-shrink-0 w-fit flex items-center gap-2 px-3 py-2 rounded-xl border border-[var(--border)] bg-white whitespace-nowrap">
              <SlidersHorizontal className="w-4 h-4 text-[var(--text-secondary)]" />
              <select
                value={ordenarPor}
                onChange={(e) => setOrdenarPor(e.target.value)}
                className="bg-transparent text-sm font-medium text-[var(--text-secondary)] focus:outline-none focus:ring-0 cursor-pointer pr-4"
                data-testid="sort-select"
              >
                <option value="distancia">Mais próximos</option>
                <option value="avaliacao">Melhor avaliados</option>
                <option value="recente">Mais recentes</option>
              </select>
            </div>

            {activeFiltersCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="ml-auto text-sm text-[var(--primary)] hover:underline flex items-center gap-1"
                data-testid="explorar-clear-filters"
              >
                <X className="w-4 h-4" />
                Limpar filtros ({activeFiltersCount})
              </button>
            )}
          </div>

          </div>{/* /collapsible secondary filters */}

        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-2 space-y-3">
        {/* Categories - Horizontal Scroll (título removido a pedido) */}
        <section>
          {selectedCategory && (
            <div className="flex items-center justify-end mb-2">
              <button
                type="button"
                onClick={() => {
                  setSelectedCategory('');
                  updateSearchParams({ categoria: null });
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--primary)] hover:underline"
                data-testid="clear-category-btn"
              >
                <X className="w-4 h-4" />
                Limpar categoria
              </button>
            </div>
          )}
          <HorizontalCarousel
            testId="explorar-categories-carousel"
            itemClassName="py-2"
          >
            {/* "Todas" pseudo-category — always first, visually distinct, lets
                the user reset the filter without hunting for the active card. */}
            <button
              type="button"
              onClick={() => {
                if (!selectedCategory) return;
                setSelectedCategory('');
                updateSearchParams({ categoria: null });
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className={`flex-shrink-0 w-24 h-24 snap-start rounded-xl border bg-white p-2 flex flex-col items-center justify-center gap-1 text-center transition-all hover:shadow-md ${
                !selectedCategory
                  ? 'ring-2 ring-[var(--primary)] border-[var(--primary)]'
                  : 'border-[var(--border)] hover:border-[var(--primary)]'
              }`}
              data-testid="category-all-btn"
            >
              <div className="w-8 h-8 rounded-full bg-[var(--primary)]/10 flex items-center justify-center">
                <Layers className="w-4 h-4 text-[var(--primary)]" />
              </div>
              <span className="text-[11px] font-medium leading-tight line-clamp-2">Todas</span>
            </button>

            {categories.map(category => (
              <div
                key={category.id}
                data-cat-id={category.id}
                className={`flex-shrink-0 w-24 h-24 snap-start rounded-xl ${selectedCategory === category.id ? 'ring-2 ring-[var(--primary)]' : ''}`}
              >
                <CategoryCard
                  category={category}
                  onClick={() => handleCategorySelect(category.id)}
                />
              </div>
            ))}
          </HorizontalCarousel>
        </section>

        {/* Results */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
              Resultados {filteredBusinesses.length > 0 && `(${filteredBusinesses.length})`}
            </h2>

            {/* Grid / List toggle */}
            <div className="inline-flex items-center rounded-lg border border-[var(--border)] bg-white overflow-hidden" role="group" aria-label="Modo de visualização">
              <button
                type="button"
                onClick={() => handleViewModeChange('grid')}
                aria-pressed={viewMode === 'grid'}
                aria-label="Visualizar em grade"
                className={`px-3 py-1.5 text-sm transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-[var(--primary)] text-white'
                    : 'text-[var(--text-secondary)] hover:bg-gray-50'
                }`}
                data-testid="view-mode-grid"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => handleViewModeChange('list')}
                aria-pressed={viewMode === 'list'}
                aria-label="Visualizar em lista"
                className={`px-3 py-1.5 text-sm transition-colors border-l border-[var(--border)] ${
                  viewMode === 'list'
                    ? 'bg-[var(--primary)] text-white'
                    : 'text-[var(--text-secondary)] hover:bg-gray-50'
                }`}
                data-testid="view-mode-list"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredBusinesses.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[var(--text-secondary)]">Nenhum negócio encontrado</p>
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="mt-2 text-[var(--primary)] hover:underline"
                >
                  Limpar filtros
                </button>
              )}
            </div>
          ) : (
            <div
              className={
                viewMode === 'grid'
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4'
                  : 'grid grid-cols-1 gap-3 max-w-3xl mx-auto'
              }
              data-testid={`results-${viewMode}`}
            >
              {filteredBusinesses.map((business, idx) => (
                <BusinessCard
                  key={`${business.id || business._id || 'b'}-${idx}`}
                  business={business}
                  showDistance={true}
                  variant={viewMode === 'grid' ? 'grid' : 'horizontal'}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}


// ============================================================================
// FiltrosRapidos — booleanos + Faixa de Preço num Popover ancorado ao trigger,
// para que o botão possa ficar inline com os demais filtros (bairro, distrito,
// contratando, ordenar).
// ============================================================================
function FiltrosRapidos({ filters, toggleFilter, setFilters, updateSearchParams }) {
  const booleanToggles = [
    { key: 'promocao', label: 'Promoções', icon: Tag },
    { key: 'aberto_agora', label: 'Aberto agora', icon: Clock },
    { key: 'entrega', label: 'Faz entrega', icon: Truck },
    { key: 'atende_domicilio', label: 'Atende em casa', icon: HomeIcon },
  ];

  const activeCount =
    booleanToggles.filter((b) => filters[b.key]).length + (filters.faixa_preco ? 1 : 0);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-fit flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[var(--border)] bg-white text-sm font-medium text-[var(--text-secondary)] hover:border-[var(--primary)] transition-all whitespace-nowrap"
          data-testid="filtros-rapidos-toggle"
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filtros rápidos
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--primary)] text-white text-xs font-semibold">
              {activeCount}
            </span>
          )}
          <ChevronDown className="w-4 h-4 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[min(92vw,520px)] p-3"
        data-testid="filtros-rapidos-panel"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {booleanToggles.map(({ key, label, icon: Icon }) => (
            <label
              key={key}
              htmlFor={`sw-${key}`}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white border border-[var(--border)] cursor-pointer hover:border-[var(--primary)]/40 transition-all"
              data-testid={`filtro-rapido-row-${key}`}
            >
              <span className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                <Icon className="w-4 h-4 text-[var(--text-secondary)]" />
                {label}
              </span>
              <Switch
                id={`sw-${key}`}
                checked={!!filters[key]}
                onCheckedChange={() => toggleFilter(key)}
                data-testid={`filter-switch-${key}`}
              />
            </label>
          ))}

          {/* Faixa de Preço — dropdown compacto, mantido conforme escolha do usuário */}
          <div
            className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white border border-[var(--border)] sm:col-span-2"
            data-testid="filtro-rapido-row-faixa_preco"
          >
            <span className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
              <DollarSign className="w-4 h-4 text-[var(--text-secondary)]" />
              Faixa de preço
            </span>
            <select
              value={filters.faixa_preco || ''}
              onChange={(e) => {
                const value = e.target.value;
                setFilters((prev) => ({ ...prev, faixa_preco: value }));
                updateSearchParams({ faixa_preco: value || null });
              }}
              className="text-sm bg-transparent border-0 focus:ring-0 focus:outline-none pr-6 font-medium text-[var(--primary)] cursor-pointer"
              data-testid="filter-price-range"
            >
              <option value="">Todas</option>
              <option value="$">$ Econômico</option>
              <option value="$$">$$ Moderado</option>
              <option value="$$$">$$$ Premium</option>
            </select>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

